// ═══════════════════════════════════════════════════════════════
// useUserData — single subscription to the signed-in mechanic's
// jobs / customers / vehicles. Used by Home.jsx and Calendar.jsx,
// both of which need the whole picture filtered different ways.
//
// All snapshot reads pass `{ serverTimestamps: 'estimate' }` so any
// pending serverTimestamp() write (startedAt, sentAt, paidAt, etc.)
// returns an ESTIMATED local Timestamp on the very first emission
// — instead of null until the server confirms ~200ms-1s later.
// Without this, the UI sees `startedAt: null` after a tap and the
// Start/Leave button stays "Start" until the server replies, which
// reads as a stale/broken button.
// ═══════════════════════════════════════════════════════════════
import { useState, useEffect, useMemo } from 'react';
import { onSnapshot, query, orderBy } from 'firebase/firestore';
import { customersCol, jobsCol, vehiclesCol } from '../database.js';
import { activeCustomers } from './job-helpers.js';

const SNAP_OPTS = { serverTimestamps: 'estimate' };

// Returns BOTH lists on purpose:
//
//   customers     — active only. What every list, picker, dashboard
//                   and name lookup should use.
//   allCustomers  — active AND archived. Only for two jobs: deciding
//                   whether a job is an ORPHAN (its customer no longer
//                   exists at all), and exporting.
//
// Get that backwards and Home offers to permanently delete every
// archived customer's jobs, which would make archiving worse than the
// cascade delete it replaced. See the note on activeCustomers().
export function useUserData() {
  const [jobs, setJobs] = useState([]);
  const [allCustomers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let loadedJobs = false, loadedCustomers = false, loadedVehicles = false;
    const markReady = () => {
      if (loadedJobs && loadedCustomers && loadedVehicles) setReady(true);
    };

    const unsubJobs = onSnapshot(
      query(jobsCol(), orderBy('createdAt', 'desc')),
      (snap) => {
        setJobs(snap.docs.map((d) => ({ id: d.id, ...d.data(SNAP_OPTS) })));
        loadedJobs = true; markReady();
      },
      (err) => { console.error('Jobs load failed:', err); loadedJobs = true; markReady(); }
    );
    const unsubCustomers = onSnapshot(
      query(customersCol(), orderBy('name', 'asc')),
      (snap) => {
        setCustomers(snap.docs.map((d) => ({ id: d.id, ...d.data(SNAP_OPTS) })));
        loadedCustomers = true; markReady();
      },
      (err) => { console.error('Customers load failed:', err); loadedCustomers = true; markReady(); }
    );
    const unsubVehicles = onSnapshot(
      query(vehiclesCol()),
      (snap) => {
        setVehicles(snap.docs.map((d) => ({ id: d.id, ...d.data(SNAP_OPTS) })));
        loadedVehicles = true; markReady();
      },
      (err) => { console.error('Vehicles load failed:', err); loadedVehicles = true; markReady(); }
    );
    return () => { unsubJobs(); unsubCustomers(); unsubVehicles(); };
  }, []);

  const customers = useMemo(() => activeCustomers(allCustomers), [allCustomers]);

  return { jobs, customers, allCustomers, vehicles, ready };
}
